import { Platform, StyleSheet, View } from 'react-native';

import { MenuErrorFallback } from '../components/menu-error-fallback';
import { MenuListSkeletonLoading } from '../components/menu-list-skeleton';
import { useMenuListQuery } from '../queries/use-menu-list-query';

import { ScreenMenuList } from './screen-menu-list';

const ORDER_TYPE_FOCUS_TARGET_ATTR = 'data-fhs-order-type-focus-target';

type ScreenMenuListWithQueryProps = {
  selectedRestaurantId: string;
  region: 'us' | 'ca';
  serviceMode: 'delivery' | 'pickup';
};

export function ScreenMenuListWithQuery({
  selectedRestaurantId: restaurantId,
  serviceMode,
  region,
}: ScreenMenuListWithQueryProps) {
  const { data, isError, isPending, isFetching } = useMenuListQuery({
    restaurantId,
    serviceMode,
    region,
  });
  const isMenuLoading = isPending || (!data && isFetching);
  const menuAccessibilityName = serviceMode === 'delivery' ? 'Delivery menu' : 'Pick up menu';
  const menuFocusTarget = (
    <MenuContentFocusTarget
      label={
        isMenuLoading ? `Loading ${menuAccessibilityName}. Please wait.` : menuAccessibilityName
      }
      busy={isMenuLoading}
    />
  );

  // Show skeleton during initial load or when refetching after a null response
  // This also prevents the error fallback from flashing when data is temporarily null
  if (isMenuLoading) {
    return (
      <>
        {menuFocusTarget}
        <MenuListSkeletonLoading />
      </>
    );
  }

  // TODO: add refresh logic
  if (isError) {
    return (
      <View style={{ flex: 1 }}>
        {menuFocusTarget}
        <MenuErrorFallback />
      </View>
    );
  }

  if (!data) {
    // XXX: we should log an error, this should never happen in production
    // @ts-ignore
    // console.error(
    //   'No data found for menu list. If you are seeing this, you many need to sync store data'
    // );
    return (
      <View style={{ flex: 1 }}>
        {menuFocusTarget}
        <MenuErrorFallback />
      </View>
    );
  }

  return (
    <>
      {menuFocusTarget}
      <ScreenMenuList sections={data.sections} />
    </>
  );
}

function MenuContentFocusTarget({ label, busy }: { label: string; busy: boolean }) {
  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <View
      accessible
      accessibilityLabel={label}
      style={styles.menuFocusTarget}
      {...({
        [ORDER_TYPE_FOCUS_TARGET_ATTR]: 'true',
        'aria-busy': busy,
        'aria-label': label,
        'aria-level': 1,
        'aria-live': 'polite',
        role: 'heading',
        tabIndex: -1,
      } as Record<string, unknown>)}
    />
  );
}

const styles = StyleSheet.create({
  menuFocusTarget: {
    borderWidth: 0,
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: 1,
  },
});
